import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import {
  Clock,
  Play,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
  Calendar,
  History as HistoryIcon,
  Activity,
} from 'lucide-react';

import { API_BASE_URL as API } from '../utils/apiConfig';

const fetchJSON = async (path, opts = {}) => {
  const res = await fetch(`${API}${path}`, { credentials: 'include', ...opts });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.json();
};

const fmtIST = (iso) => {
  if (!iso) return 'N/A';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' }) + ' IST';
  } catch {
    return iso;
  }
};

const fmtDuration = (sec) => {
  if (!sec) return 'N/A';
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  return `${(sec / 3600).toFixed(1)}h`;
};

export default function DailyScheduler() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        fetchJSON('/api/scheduler/status'),
        fetchJSON('/api/scheduler/history?limit=15'),
      ]);
      setStatus(s);
      setHistory(h.history || []);
    } catch (e) {
      toast.error(`Failed to load scheduler: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const handleRunNow = async () => {
    setTriggering(true);
    try {
      await fetchJSON('/api/scheduler/trigger', { method: 'POST' });
      toast.success('Sync cycle triggered. Will start within a minute.');
      setTimeout(load, 3000);
    } catch (e) {
      toast.error(`Failed to trigger: ${e.message}`);
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const lastRun = status?.last_sync_completed || status?.last_cycle_end;
  const currentStatus = status?.current_status || status?.status || 'idle';
  const isSyncing = currentStatus === 'syncing';

  return (
    <div className="space-y-6" data-testid="daily-scheduler-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="daily-scheduler-title">
            <Clock className="h-8 w-8 text-indigo-500" />
            Daily Scheduler
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Automated daily sync runs once a night at 1:00 AM IST, pulling incremental data from Data Bridge for every connected brand.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-scheduler-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleRunNow}
            disabled={triggering || isSyncing}
            className="bg-indigo-500 hover:bg-indigo-600 text-white"
            data-testid="run-now-btn"
          >
            {triggering ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Run Now
          </Button>
        </div>
      </div>

      {/* Schedule overview cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" /> Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold" data-testid="schedule-label">
              {status?.schedule || 'Daily at 1:00 AM IST'}
            </div>
            <Badge variant={status?.status === 'running' ? 'default' : 'secondary'} className="mt-2">
              {status?.status === 'running' ? 'enabled' : status?.status || 'not started'}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
              <Activity className="h-4 w-4" /> Current state
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {isSyncing && <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />}
              {!isSyncing && currentStatus === 'idle' && <CheckCircle2 className="h-5 w-5 text-green-500" />}
              <span className="text-lg font-semibold capitalize" data-testid="current-state">
                {currentStatus}
              </span>
            </div>
            {status?.stuck_syncs_reset > 0 && (
              <div className="text-xs text-amber-600 mt-1">
                {status.stuck_syncs_reset} stuck sync(s) auto-reset
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" /> Last run
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-semibold" data-testid="last-run-time">
              {fmtIST(lastRun)}
            </div>
            {status?.last_cycle_duration_seconds && (
              <div className="text-xs text-muted-foreground mt-1">
                Duration: {fmtDuration(status.last_cycle_duration_seconds)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
              <CheckCircle2 className="h-4 w-4" /> Last result
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-1" data-testid="last-result">
              <span className="text-2xl font-bold text-green-600">
                {status?.last_cycle_successful || 0}
              </span>
              <span className="text-muted-foreground">/</span>
              <span className="text-lg text-muted-foreground">
                {status?.last_cycle_brands || 0}
              </span>
              <span className="text-xs text-muted-foreground ml-1">brands ok</span>
            </div>
            {status?.last_cycle_failed > 0 && (
              <div className="text-xs text-red-600 mt-1">
                {status.last_cycle_failed} failed
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <HistoryIcon className="h-5 w-5" />
            Run History (last {history.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8">
              No history yet — the first run will appear here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="scheduler-history-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2">Started (IST)</th>
                    <th className="text-left p-2">Ended (IST)</th>
                    <th className="text-right p-2">Duration</th>
                    <th className="text-right p-2">Brands</th>
                    <th className="text-right p-2">Success</th>
                    <th className="text-right p-2">Failed</th>
                    <th className="text-left p-2">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, idx) => (
                    <tr key={idx} className="border-t hover:bg-muted/30" data-testid={`history-row-${idx}`}>
                      <td className="p-2">{fmtIST(h.cycle_start)}</td>
                      <td className="p-2">{fmtIST(h.cycle_end)}</td>
                      <td className="p-2 text-right">{fmtDuration(h.duration_seconds)}</td>
                      <td className="p-2 text-right">{h.total_brands || 0}</td>
                      <td className="p-2 text-right">
                        <Badge className="bg-green-600 text-white">{h.successful || 0}</Badge>
                      </td>
                      <td className="p-2 text-right">
                        {h.failed > 0 ? <Badge variant="destructive">{h.failed}</Badge> : <span className="text-muted-foreground">0</span>}
                      </td>
                      <td className="p-2">
                        <Badge variant={h.force_full ? 'default' : 'secondary'} className="text-xs">
                          {h.force_full ? 'full' : 'incremental'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-brand last results */}
      {status?.last_cycle_end && history[0]?.results && history[0].results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Last cycle — per-brand results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2">Brand</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Last txn</th>
                    <th className="text-left p-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {history[0].results.map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2 font-medium">{r.brand_name}</td>
                      <td className="p-2">
                        {r.success ? <CheckCircle2 className="h-4 w-4 text-green-600 inline" /> : <XCircle className="h-4 w-4 text-red-600 inline" />}
                      </td>
                      <td className="p-2 text-muted-foreground">{r.last_transaction || 'N/A'}</td>
                      <td className="p-2 text-red-600 text-xs">{r.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
